/**
 * Toast — eDMS Design System (UX bolder-refresh, 2026-05-29)
 *
 * Runtime notification system. The app had a NotificationInbox *page* but no
 * in-app feedback when an action succeeded or failed — every mutation was
 * silent or surfaced an inline banner. This is the single highest-ROI
 * "feels premium" primitive.
 *
 * Usage:
 *   // once, at the app root:
 *   <ToastProvider><App /></ToastProvider>
 *
 *   // anywhere below it:
 *   const toast = useToast();
 *   toast.success('Period locked', 'Q1-2026 is now closed.');
 *   toast.error('Upload failed', err.message);
 *   const id = toast.info('Working…', undefined, { duration: 0 }); // sticky
 *   toast.dismiss(id);
 *
 * Accessibility:
 * - The region is aria-live; success/info are 'polite', error/warning are
 *   'assertive' so screen readers announce failures immediately.
 * - Each toast is role="status" (polite) or role="alert" (assertive).
 * - Dismiss button has an aria-label; Esc dismisses the most recent toast.
 * - Honors prefers-reduced-motion (no slide, just fade) via the CSS module.
 *
 * RTL: the region anchors to inset-inline-end / inset-block-start, so it
 * sits top-right in LTR and top-left in RTL automatically.
 */

import {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import styles from './Toast.module.css';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  /** Auto-dismiss after N ms. 0 = sticky (manual dismiss only). Default: 5000 (success/info), 8000 (warning), 0 (error). */
  duration?: number;
}

export interface ToastRecord {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  duration: number;
  createdAt: number;
}

export interface ToastApi {
  toast: (variant: ToastVariant, title: string, message?: string, opts?: ToastOptions) => string;
  success: (title: string, message?: string, opts?: ToastOptions) => string;
  error: (title: string, message?: string, opts?: ToastOptions) => string;
  info: (title: string, message?: string, opts?: ToastOptions) => string;
  warning: (title: string, message?: string, opts?: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Default auto-dismiss by variant. Errors are sticky so failures aren't missed. */
function defaultDuration(variant: ToastVariant): number {
  switch (variant) {
    case 'success': return 5000;
    case 'info':    return 5000;
    case 'warning': return 8000;
    case 'error':   return 0;      // sticky — user dismisses
  }
}

const ICON: Record<ToastVariant, string> = {
  success: '✓',   // ✓
  error:   '✕',   // ✕
  info:    'ℹ',   // ℹ
  warning: '⚠',   // ⚠
};

let _seq = 0;
function nextId(): string {
  _seq += 1;
  return `toast-${Date.now()}-${_seq}`;
}

const MAX_VISIBLE = 5;

export interface ToastProviderProps {
  children: ReactNode;
  /** Max simultaneously-visible toasts (older ones drop off). Default 5. */
  max?: number;
}

export function ToastProvider({ children, max = MAX_VISIBLE }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
    for (const tm of timers.current.values()) clearTimeout(tm);
    timers.current.clear();
  }, []);

  const push = useCallback((variant: ToastVariant, title: string, message?: string, opts?: ToastOptions): string => {
    const id = nextId();
    const duration = opts?.duration ?? defaultDuration(variant);
    const rec: ToastRecord = { id, variant, title, message, duration, createdAt: Date.now() };
    setToasts((cur) => {
      const next = [...cur, rec];
      // Drop oldest beyond the cap.
      return next.length > max ? next.slice(next.length - max) : next;
    });
    if (duration > 0) {
      const tm = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, tm);
    }
    return id;
  }, [dismiss, max]);

  const api = useMemo<ToastApi>(() => ({
    toast: push,
    success: (t, m, o) => push('success', t, m, o),
    error:   (t, m, o) => push('error',   t, m, o),
    info:    (t, m, o) => push('info',    t, m, o),
    warning: (t, m, o) => push('warning', t, m, o),
    dismiss,
    dismissAll,
  }), [push, dismiss, dismissAll]);

  // Esc dismisses the most-recent toast.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const latest = toasts[toasts.length - 1];
      if (e.key === 'Escape' && latest) {
        dismiss(latest.id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toasts, dismiss]);

  // Clean up timers on unmount.
  useEffect(() => () => { for (const tm of timers.current.values()) clearTimeout(tm); }, []);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {portalTarget && createPortal(
        <div className={styles.region} aria-live="polite" aria-relevant="additions">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={clsx(styles.toast, styles[t.variant])}
              role={t.variant === 'error' || t.variant === 'warning' ? 'alert' : 'status'}
            >
              <span className={styles.icon} aria-hidden="true">{ICON[t.variant]}</span>
              <div className={styles.body}>
                <span className={styles.title}>{t.title}</span>
                {t.message && <span className={styles.message}>{t.message}</span>}
              </div>
              <button
                type="button"
                className={styles.close}
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
              >
                {'✕'}
              </button>
            </div>
          ))}
        </div>,
        portalTarget,
      )}
    </ToastContext.Provider>
  );
}

/**
 * Access the toast API. Throws if used outside a ToastProvider so the
 * mistake is caught at dev time rather than silently no-op'ing.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() must be used within a <ToastProvider>');
  return ctx;
}
